// exportRunStore.js — state of the Export tab's run, at module level (same reason as scanExportStore.js:
// tabs unmount, the run must not depend on one). Read by ExportTab (its bar and tiles), by RenderBar /
// ScanExportDock (the run from other tabs) through components/useBackgroundRuns.js.
//
// The Studio cleanup ("drop the renders once a clean export finished") also lives here, NOT in the
// tab: it used to run only when the user clicked "Back to Studio", so leaving by the tab bar kept stale
// renders in Studio. Now it runs when the run finishes; if the Export tab is open at that moment it is
// held until the tab is left (the done screen stays), by any route.
import { useSyncExternalStore } from 'react';

const IDLE = {
  running: false, finished: false,            // finished = the tab's "done" view (confirmation / retry)
  done: 0, total: 0, current: '', phase: '',
  exportingKey: null, exportedKeys: new Set(), errorKeys: new Set(), errors: [],
  unseen: false, summary: '',                 // unseen = ended while the user was elsewhere (RenderBar / dock line)
};
let state = IDLE;
let claimed = false;
let present = 0;                  // ExportTab instances mounted
let pending = null;               // cleanup to run when the tab is left
const listeners = new Set();

export function setRun(patch) {
  state = typeof patch === 'function' ? patch(state) : Object.assign({}, state, patch);
  listeners.forEach(l => l());
}
export function getRun() { return state; }
export function useExportRun() { return useSyncExternalStore(l => { listeners.add(l); return () => listeners.delete(l); }, getRun, getRun); }
export function isExportTabOpen() { return present > 0; }

// One run at a time (taken at click time, before the folder picker).
export function claimExportRun() { if (state.running || claimed) return false; claimed = true; return true; }
export function releaseExportRun() { claimed = false; }
export function beginRun(total) {
  claimed = false; pending = null;
  setRun({ running: true, finished: false, done: 0, total, current: '', phase: '', exportingKey: null, errorKeys: new Set(), errors: [], unseen: false, summary: '' });
}
export function addExported(k) { setRun(s => Object.assign({}, s, { exportedKeys: new Set(s.exportedKeys).add(k) })); }
export function addErrorKey(k) { setRun(s => Object.assign({}, s, { errorKeys: new Set(s.errorKeys).add(k) })); }

// The Export tab calls this when it resets itself (Clear all, Back to Studio after a clean export).
export function resetRunView() {
  if (state.running) return;
  setRun(s => Object.assign({}, s, { finished: false, done: 0, total: 0, current: '', phase: '', exportingKey: null, exportedKeys: new Set(), errorKeys: new Set(), errors: [] }));
}
// New files dropped after a run: the "done" view (confirmation / retry) goes away, the tiles' marks stay.
export function clearDone() { if (!state.running) setRun({ finished: false, errors: [] }); }
export function dismissExportUnseen() { setRun({ unseen: false }); }

// Finish: a clean run asks Studio to drop its renders — now when the tab is away, on leaving when it is open.
export function finishRun({ done, total, errors, onFinished }) {
  const clean = errors.length === 0;
  const summary = clean ? `${total} file${total === 1 ? '' : 's'} exported` : `${done} / ${total} exported · ${errors.length} failed`;
  setRun(s => Object.assign({}, s, { running: false, finished: true, done, total, current: '', phase: 'done', exportingKey: null, errors, summary, unseen: present === 0 }));
  if (clean && typeof onFinished === 'function') {
    if (present > 0) pending = onFinished;
    else { onFinished(); resetRunView(); }
  }
}
// Back to Studio already cleans after a clean export; drop the pending one so it does not run twice.
export function consumePending() { pending = null; }
export function registerExportTab() {
  present += 1; setRun({ unseen: false });
  return () => {
    present = Math.max(0, present - 1);
    if (present === 0 && pending) { const f = pending; pending = null; f(); resetRunView(); }
    setRun({});   // the bars read isExportTabOpen(): let them re-render
  };
}
